import * as Yup from 'yup'
import React from 'react'
import { components } from 'react-select'

import { FormItemProps } from '@/components/Form/types'
import ImageComponent from '@/components/UiElements/ImageComponent'
import getAllProducts from '@/services/ProductService/getAllProducts'
import store from '@/store'
import { ProductData } from '@/types/product'

const ProductThumbnail = ({ product, size = 'md' }: { product: any; size?: 'sm' | 'md' }) => {
  const image = product?.['2d_image_url']
  const className = size === 'sm' ? 'w-[18px] h-[18px] min-w-[18px]' : 'w-[36px] h-[36px] min-w-[36px]'

  return (
    <div
      className={`${className} rounded-sm bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center`}
    >
      {image ? (
        <ImageComponent src={image} className="!w-full !h-full" style={{ width: '100%', height: '100%' }} />
      ) : (
        <span className="font-icon-regular text-gray-300 text-[12px]">image</span>
      )}
    </div>
  )
}

const ProductOption = (props: any) => {
  const product = props.data?.data || props.data
  const sectionCount = product?.sections?.length || 0

  return (
    <components.Option {...props}>
      <div className="flex items-center gap-sm min-w-0">
        <ProductThumbnail product={product} />
        <div className="flex flex-col min-w-0">
          <span className="truncate">{props.data?.label}</span>
          <span className="text-text-xs text-gray-400 truncate">
            {product?.sku}
            {sectionCount ? ` · ${sectionCount} section${sectionCount > 1 ? 's' : ''}` : ' · no sections'}
          </span>
        </div>
      </div>
    </components.Option>
  )
}

const ProductSingleValue = (props: any) => {
  const product = props.data?.data || props.data

  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-xs min-w-0">
        <ProductThumbnail product={product} size="sm" />
        <span className="truncate">{props.data?.label}</span>
      </div>
    </components.SingleValue>
  )
}

/**
 * Only products with an active profile have sections to draw on, so those are
 * the ones offered. The search runs over the cached catalogue rather than the
 * API, matching how the products list filters.
 */
const fetchDesignableProducts = async (inputValue?: string | null) => {
  let products = store.products as ProductData[]
  if (!products.length) products = (await getAllProducts()) || []

  const text = (inputValue || '').trim().toLowerCase()

  return products
    .filter((product) => product.commerce_status !== 'removed')
    .filter((product) => product.is_product_profile_active && product.product_profile_id)
    .filter((product) => {
      if (!text) return true
      return `${product.name} ${product.sku} ${product.variant_name || ''}`.toLowerCase().includes(text)
    })
    .slice(0, 100)
    .map((product) => ({
      label: product.name,
      value: product.sku,
      data: product,
    }))
}

const Items: Array<FormItemProps> = [
  {
    name: 'name',
    label: 'Name',
    placeholder: 'Enter template name',
    type: 'Text',
    help: 'The name customers see when choosing a template, such as Award Plate or Wedding Gift.',
    defaultValue: null,
    validation: Yup.string().required('Name is required'),
    classNames: { container: 'col-span-12' },
    autoFocus: true,
  },
  {
    name: 'product',
    label: 'Design on product',
    placeholder: 'Search by product name or SKU',
    type: 'Select',
    help: 'The product you draw this template on. The design is resized to fit whichever section a customer applies it to later, so this is the starting canvas rather than a restriction. Only products with an active profile are listed, since those are the ones with sections.',
    itemOptions: {
      fetch: fetchDesignableProducts,
      features: { needToUpdateOnOpen: true },
      clearable: true,
      components: {
        Option: ProductOption,
        SingleValue: ProductSingleValue,
      },
    },
    defaultValue: null,
    validation: Yup.object().required('Pick a product to design on').typeError('Pick a product to design on'),
    classNames: { container: 'col-span-12' },
  },
  {
    name: 'description',
    label: 'Description',
    placeholder: 'Describe when to use this template',
    type: 'LongText',
    help: 'Explain what this template is for, so other admins know when to reach for it.',
    defaultValue: null,
    classNames: { container: 'col-span-12 break-line' },
    itemOptions: { rows: 3 },
  },
  {
    name: 'isActive',
    label: 'Active',
    type: 'Checkbox',
    help: 'Turn this on when the template is ready for customers. Turn it off to keep it saved but hidden.',
    defaultValue: true,
    classNames: { container: 'col-span-6 !justify-center' },
  },
  {
    name: 'isGeneric',
    label: 'Available everywhere',
    type: 'Checkbox',
    help: 'Leave this on to offer the template on every section. Turn it off to limit it to chosen sections, then pick it in the Specific Design Templates field on each product profile section.',
    defaultValue: true,
    classNames: { container: 'col-span-6 !justify-center' },
  },
]

export default Items
